
/**
 * Security utilities for the application
 */

// CSRF token management
export const generateCsrfToken = (): string => {
  return crypto.randomUUID();
};

export const storeCsrfToken = (key: string, token: string): void => {
  sessionStorage.setItem(key, token);
};

export const verifyCsrfToken = (key: string, token: string): boolean => {
  return sessionStorage.getItem(key) === token;
};

// Rate limiting
interface RateLimitOptions {
  maxAttempts: number;
  timeWindow: number; // in milliseconds
  lockoutPeriod: number; // in milliseconds
}

export const checkRateLimit = (
  key: string,
  options: RateLimitOptions = { maxAttempts: 5, timeWindow: 60000, lockoutPeriod: 300000 }
): { allowed: boolean; remainingTime?: number } => {
  const attemptsKey = `${key}_attempts`;
  const lastAttemptKey = `${key}_last_attempt`;
  const lockoutKey = `${key}_lockout_until`;
  
  // Check if user is in lockout period
  const lockoutUntil = sessionStorage.getItem(lockoutKey);
  if (lockoutUntil) {
    const lockoutTime = parseInt(lockoutUntil, 10);
    if (Date.now() < lockoutTime) {
      return { 
        allowed: false, 
        remainingTime: Math.ceil((lockoutTime - Date.now()) / 1000) 
      };
    } else {
      // Lockout period expired, clear it
      sessionStorage.removeItem(lockoutKey);
      sessionStorage.removeItem(attemptsKey);
    }
  }

  // Get and update attempt count
  const attempts = parseInt(sessionStorage.getItem(attemptsKey) || '0', 10);
  sessionStorage.setItem(lastAttemptKey, Date.now().toString());
  
  // If under the limit, allow and increment
  if (attempts < options.maxAttempts) {
    sessionStorage.setItem(attemptsKey, (attempts + 1).toString());
    return { allowed: true };
  }
  
  // Check if time window has passed since first attempt
  const lastAttempt = parseInt(sessionStorage.getItem(lastAttemptKey) || '0', 10);
  if (Date.now() - lastAttempt > options.timeWindow) {
    // Reset if time window passed
    sessionStorage.setItem(attemptsKey, '1');
    return { allowed: true };
  }
  
  // Set lockout period
  const lockoutTime = Date.now() + options.lockoutPeriod;
  sessionStorage.setItem(lockoutKey, lockoutTime.toString());
  
  return { 
    allowed: false, 
    remainingTime: Math.ceil(options.lockoutPeriod / 1000) 
  };
};

// Input sanitization
export const sanitizeInput = (input: string): string => {
  if (!input) return '';
  return input.replace(/[<>"'&]/g, '');
};

// Safe storage utilities
export const safelyStoreData = (key: string, data: any, sensitive: boolean = false): void => {
  if (sensitive) {
    // For sensitive data, use sessionStorage which is cleared when the browser is closed
    sessionStorage.setItem(key, JSON.stringify(data));
  } else {
    localStorage.setItem(key, JSON.stringify(data));
  }
};

export const safelyRetrieveData = (key: string, sensitive: boolean = false): any => {
  try {
    const storage = sensitive ? sessionStorage : localStorage;
    const item = storage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch (error) {
    console.error(`Error retrieving data for key ${key}:`, error);
    return null;
  }
};

// Security cleanup
export const clearAuthData = (): void => {
  // Clear any auth-related data from storage
  Object.keys(localStorage).forEach((key) => {
    if (key.includes('auth') || key.includes('token') || key.includes('user')) {
      localStorage.removeItem(key);
    }
  });
  
  Object.keys(sessionStorage).forEach((key) => {
    if (key.includes('auth') || key.includes('token') || key.includes('user')) {
      sessionStorage.removeItem(key);
    }
  });
  
  // Clear cookies related to auth
  document.cookie.split(';').forEach((c) => {
    const cookieName = c.trim().split('=')[0];
    if (cookieName.includes('auth') || cookieName.includes('token')) {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; secure; sameSite=strict`;
    }
  });
};
